import React, { FC, useCallback, useMemo, useState } from 'react';

import { SearchResponseLevel } from '@ariksa/inventory-core';
import { HStack, Stack, Switch, Text } from '@chakra-ui/react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { Select } from 'components/DataEntry';
import { ServicesIcon } from 'components/Icons';
import { useCloudAccountId } from 'containers/Setup/CloudAccounts/utils';
import { SidebarAccordion } from 'containers/Visibility/SecurityGraphNext/Components/SidebarAccordion';
import { SourceAccountFilter } from 'containers/Visibility/SecurityGraphNext/Components/SourceAccountFilter';
import { SourceEntityMenuList } from 'containers/Visibility/SecurityGraphNext/Components/SourceEntityMenuList';
import { SourceGroupLabel } from 'containers/Visibility/SecurityGraphNext/Components/SourceGroupLabel';
import { useRedirectInfo } from 'containers/Visibility/SecurityGraphNext/hooks/useRedirectInfo';
import { useSearchResult } from 'containers/Visibility/SecurityGraphNext/hooks/useSearchResult';
import { useSourceNodes } from 'containers/Visibility/SecurityGraphNext/hooks/useSourceNodes';
import { useSourcePages } from 'containers/Visibility/SecurityGraphNext/hooks/useSourcePages';
import { selectSecurityGraphNext } from 'containers/Visibility/SecurityGraphNext/selectors';
import { actions } from 'containers/Visibility/SecurityGraphNext/slice';
import { SecurityGraphMapType } from 'containers/Visibility/SecurityGraphNext/types';
// the account picker shares the entity dropdown's look
import { sourceEntitySelectStyles } from 'containers/Visibility/SecurityGraphNext/utils/sourceEntitySelectStyles';
import { SourceMenuTextContext } from 'containers/Visibility/SecurityGraphNext/utils/sourceMenuContext';
import {
  countSourceOptions,
  pickedAccountKey,
  sourceAccountFacets,
  sourceMenuText,
  withAccountLines,
} from 'containers/Visibility/SecurityGraphNext/utils/sourceOptions';

interface ISelectSourceEntity {}

// the account picker shares the type groups' header
const formatGroupLabel = data => <SourceGroupLabel label={data.label} />;

export const SelectSourceEntity: FC<ISelectSourceEntity> = props => {
  const dispatch = useDispatch();
  const {
    sourceNodeOptions,
    selectedSourceNodeOption,
    updateSelectedSourceNodeOption,
  } = useSourceNodes();
  const { responseLevel, mapType, activeSearchQueryTerm } = useSelector(
    selectSecurityGraphNext,
  );
  const { searchResult } = useSearchResult();
  const navigate = useNavigate();
  const params = new URLSearchParams(window.location.search);
  const { redirectInfo } = useRedirectInfo();
  const {
    isPaged,
    paging,
    isLoadingPage,
    searchMeta,
    loadNextPage,
    searchSources,
    pickAccounts,
  } = useSourcePages();
  const { accountMapping } = useCloudAccountId();
  const [inputValue, setInputValue] = useState('');

  // The account picker lists the server's account counts for the whole list;
  // picking one reloads the list for it, and the paging request keeps it.
  const accountFacets = useMemo(
    () => sourceAccountFacets(paging?.accounts ?? [], accountMapping),
    [accountMapping, paging?.accounts],
  );
  const accountKey = pickedAccountKey(paging?.request, accountFacets);

  const limitedOptions = useMemo(() => {
    // each entry shows its principal's cloud account under the name
    return withAccountLines(sourceNodeOptions ?? [], accountMapping)?.map(
      group => {
        if (!group.options?.length) {
          return {
            ...group.options?.[0],
            options: group.options?.slice(0, group.options?.length ?? 1),
          };
        }

        return group;
      },
    );
  }, [accountMapping, sourceNodeOptions]);

  // the menu's count header and scroll footer for a paged list
  const menuText = useMemo(() => {
    const search =
      inputValue && searchMeta?.term === inputValue ? searchMeta : undefined;
    return sourceMenuText({
      paging,
      loaded: countSourceOptions(sourceNodeOptions ?? []),
      accountLabel: accountFacets.find(facet => facet.key === accountKey)
        ?.label,
      isLoading: isLoadingPage,
      search,
    });
  }, [
    accountFacets,
    accountKey,
    inputValue,
    isLoadingPage,
    paging,
    searchMeta,
    sourceNodeOptions,
  ]);

  const handleOnLoadOptions = useCallback(
    (term: string, cb) => {
      // a paged list is searched on the server, across every type
      if (isPaged) {
        return searchSources(term, options =>
          cb(withAccountLines(options, accountMapping)),
        );
      }
      const sourceGroup = sourceNodeOptions?.[0];
      const options = sourceGroup?.options?.filter(option =>
        option?.label?.toLowerCase().includes(term.toLowerCase()),
      );

      // Return a real empty result so AsyncSelect replaces the previous list.
      cb(options?.length ? [{ ...sourceGroup, options }] : []);
    },
    [accountMapping, isPaged, searchSources, sourceNodeOptions],
  );

  const handleOnSelect = selected => {
    // for identity map, when the search query term is present, we need to pass it in next api call
    const isResourceMapPick =
      !!redirectInfo?.identity && !!activeSearchQueryTerm?.query;

    if (isResourceMapPick) {
      params.set('source_resource_uuid', selected!.value.uuid);
      params.set('query', activeSearchQueryTerm!.query!);
    } else {
      params.set('source_resource_uuid', selected!.value.uuid);
    }

    if (isResourceMapPick) {
      // A pick on the resource map becomes the identity query for that
      // principal, as the URL's `query` says. Keeping the resource map's
      // redirect info here sent /search/map with the principal as the
      // resource, which emptied the list and the map. The URL's query
      // triggers the one /search/ request.
      dispatch(
        actions.updateRedirectInfo({
          query: activeSearchQueryTerm!.query!,
          source_uuid: selected!.value.uuid,
          redirect_url: redirectInfo?.redirect_url,
          response_level: redirectInfo?.response_level,
          ai_search: redirectInfo?.ai_search,
          search_uuid: redirectInfo?.search_uuid,
          identity: true,
          account_id: redirectInfo?.account_id,
        }),
      );
    } else {
      // update redirect info with selected source node
      // NOTE: without this line, the selected source node will be updated after the api call for the search graph
      dispatch(
        actions.updateRedirectInfo({
          ...redirectInfo,
          source_uuid: selected!.value.uuid,
        }),
      );

      dispatch(actions.updateFilterChanged(true));
    }

    const url = `${window.location.pathname}?${params.toString()}`;
    navigate(url);
    updateSelectedSourceNodeOption(selected);
  };

  return (
    <Stack spacing={3}>
      <SidebarAccordion
        header={'SELECT ENTITY'}
        icon={<ServicesIcon color="primary" />}
      >
        <Stack spacing={4}>
          {accountFacets.length > 1 && (
            <SourceAccountFilter
              facets={accountFacets}
              // on a resource map, the resource's own account; principals in
              // any other account reach it across accounts
              resourceAccountId={
                redirectInfo?.identity
                  ? paging?.request.account_id?.[0]
                  : undefined
              }
              value={accountKey}
              onChange={key =>
                pickAccounts(
                  accountFacets.find(facet => facet.key === key)?.ids ?? [],
                )
              }
            />
          )}
          <SourceMenuTextContext.Provider value={menuText}>
            <Select
              async
              isDisabled={searchResult.isLoading}
              value={selectedSourceNodeOption}
              options={limitedOptions}
              cacheOptions={false}
              loadOptions={handleOnLoadOptions}
              onChange={handleOnSelect}
              // a paged list loads its next page at the bottom of the menu
              onMenuScrollToBottom={loadNextPage}
              formatGroupLabel={formatGroupLabel}
              menuPortalTarget={document.body}
              showIconInValueContainer
              showClearIndicator={false}
              styles={sourceEntitySelectStyles}
              isAFilter
              onInputChange={value => setInputValue(value)}
              components={{ MenuList: SourceEntityMenuList }}
            />
          </SourceMenuTextContext.Provider>

          {mapType === SecurityGraphMapType.Access && (
            <Stack spacing={3}>
              <HStack>
                <Switch
                  size={'sm'}
                  isChecked={responseLevel === SearchResponseLevel.Full}
                  onChange={e => {
                    dispatch(
                      actions.updateSearchResponseLevel(
                        e.target.checked
                          ? SearchResponseLevel.Full
                          : SearchResponseLevel.Compact,
                      ),
                    );
                    dispatch(
                      actions.updateRedirectInfoField({
                        response_level: e.target.checked
                          ? SearchResponseLevel.Full
                          : SearchResponseLevel.Compact,
                      }),
                    );
                    dispatch(actions.updateFilterChanged(true));
                    params.set(
                      'response_level',
                      e.target.checked ? 'full' : 'compact',
                    );

                    const url = `${
                      window.location.pathname
                    }?${params.toString()}`;
                    navigate(url);
                  }}
                />
                <Text>Show more details</Text>
              </HStack>
              {/*<HStack>*/}
              {/*  <Switch size={'sm'} />*/}
              {/*  <Text>Show Deny Map</Text>*/}
              {/*</HStack>*/}
            </Stack>
          )}
        </Stack>
      </SidebarAccordion>
    </Stack>
  );
};
